import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { PreviewSizeContext } from '../preview-size';
import { calculateScale } from '../utils/calculate-canvas-transformation';
import { Rect } from '../utils/fit-element-size-in-container';
import { getEffectiveTranslation } from '../utils/get-effective-translation';

const MIDDLE_MOUSE_BUTTON = 1;
const LEFT_MOUSE_BUTTON = 0;

interface UseHandToolOptions {
  canvasRef: React.RefObject<HTMLDivElement | null>;
  canvasSize: Rect | null;
  compositionWidth: number;
  compositionHeight: number;
  isHandToolMode: boolean;
}

interface UseHandToolReturn {
  /** 핸드툴 드래그 중 여부 - 커서 스타일 변경에 사용 */
  isPanning: boolean;
}

/**
 * 마우스 중간 버튼 드래그로 캔버스 이동하는 핸드툴.
 * 추후 버튼 클릭으로 핸드툴 모드 활성화 확장 가능.
 */
export const useHandTool = ({
  canvasRef,
  canvasSize,
  compositionWidth,
  compositionHeight,
  isHandToolMode,
}: UseHandToolOptions): UseHandToolReturn => {
  const { setSize } = useContext(PreviewSizeContext);

  const [isPanning, setIsPanning] = useState(false);
  const lastMousePosition = useRef<{ x: number; y: number } | null>(null);

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      const isMiddleButton = e.button === MIDDLE_MOUSE_BUTTON;
      const isLeftButtonInHandMode =
        isHandToolMode && e.button === LEFT_MOUSE_BUTTON;

      if (!isMiddleButton && !isLeftButtonInHandMode) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      setIsPanning(true);
      lastMousePosition.current = { x: e.clientX, y: e.clientY };
    },
    [isHandToolMode],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isPanning || !lastMousePosition.current || !canvasSize) {
        return;
      }

      const deltaX = e.clientX - lastMousePosition.current.x;
      const deltaY = e.clientY - lastMousePosition.current.y;

      lastMousePosition.current = { x: e.clientX, y: e.clientY };

      setSize((prevSize) => {
        const scale = calculateScale({
          canvasSize,
          compositionHeight,
          compositionWidth,
          previewSize: prevSize.size,
        });

        return {
          ...prevSize,
          translation: getEffectiveTranslation({
            translation: {
              x: prevSize.translation.x - deltaX,
              y: prevSize.translation.y - deltaY,
            },
            canvasSize,
            compositionHeight,
            compositionWidth,
            scale,
          }),
        };
      });
    },
    [isPanning, canvasSize, compositionHeight, compositionWidth, setSize],
  );

  const handleMouseUp = useCallback(
    (e: MouseEvent) => {
      const isMiddleButton = e.button === MIDDLE_MOUSE_BUTTON;
      const isLeftButtonInHandMode =
        isHandToolMode && e.button === LEFT_MOUSE_BUTTON;

      if (!isMiddleButton && !isLeftButtonInHandMode) {
        return;
      }

      setIsPanning(false);
      lastMousePosition.current = null;
    },
    [isHandToolMode],
  );

  useEffect(() => {
    const { current } = canvasRef;
    if (!current) {
      return;
    }

    current.addEventListener('mousedown', handleMouseDown, true);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp, true);

    return () => {
      current.removeEventListener('mousedown', handleMouseDown, true);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp, true);
    };
  }, [canvasRef, handleMouseDown, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (!isPanning) {
      return;
    }

    const preventMiddleClickScroll = (e: MouseEvent) => {
      if (e.button === MIDDLE_MOUSE_BUTTON) {
        e.preventDefault();
      }
    };

    window.addEventListener('auxclick', preventMiddleClickScroll);

    return () => {
      window.removeEventListener('auxclick', preventMiddleClickScroll);
    };
  }, [isPanning]);

  return {
    isPanning,
  };
};
